/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import {
  api,
  useGetHistoryPowerCurveQuery,
  useGetStationPowerCurveQuery,
  useGetStationStatusQuery,
} from '../../redux/api';
import { isDcPort } from '../../utils/utils';
import { getSocColors } from '../../utils/socColor';
import styles from './HistoryPowerCurve.module.css';
import { HISTORY_CHART_FONT } from './historyChartFont';
import { computeSessionAvgPowerKw, formatSocSummaryLine, isFlatSocImplausible } from './historyChartImpact';
import {
  createPowerChartExternalTooltip,
  createPowerChartHoverCrosshairPlugin,
  createPowerChartTouchReleasePlugin,
  hidePowerChartTooltip,
} from '../../utils/powerChartExternalTooltip';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
const IN_PROGRESS_POWER_CURVE_POLL_MS = 15000;
const IN_PROGRESS_STATUS_POLL_MS = 2000;
/** Same power series colors as charging page Chart.jsx / --b2b-accent */
const POWER_CHART_BORDER = 'rgba(249, 76, 175, 0.88)';
const POWER_CHART_FILL_TOP = 'rgba(249, 76, 175, 0.34)';
const POWER_CHART_FILL_BOTTOM = 'rgba(249, 76, 175, 0.02)';
const POWER_CHART_POINT = '#f94caf';
const POWER_CHART_POINT_BORDER = '#ff9ed4';

function formatTimestampLabel(timestamp) {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatElapsedMinutesLabel(timestamp, startTimestamp) {
  if (!timestamp || !startTimestamp) {
    return '';
  }

  const startMs = new Date(startTimestamp).getTime();
  const currentMs = new Date(timestamp).getTime();

  if (!Number.isFinite(startMs) || !Number.isFinite(currentMs)) {
    return '';
  }

  const elapsedMinutes = Math.max(0, Math.round((currentMs - startMs) / 60000));
  return `${elapsedMinutes}m`;
}

function getElapsedMinutesValue(timestamp, startTimestamp) {
  if (!timestamp || !startTimestamp) {
    return null;
  }

  const startMs = new Date(startTimestamp).getTime();
  const currentMs = new Date(timestamp).getTime();

  if (!Number.isFinite(startMs) || !Number.isFinite(currentMs)) {
    return null;
  }

  return Math.max(0, Math.round((currentMs - startMs) / 60000));
}

function normalizeSeries(points, divisor = 1) {
  return (points || []).map(point => {
    const rawValue = point?.value;
    return {
      timestamp: point?.timestamp || null,
      label: formatTimestampLabel(point?.timestamp),
      value: typeof rawValue === 'number' ? rawValue / divisor : null,
    };
  });
}

function getValidValues(points) {
  return (points || []).map(point => point?.value).filter(value => typeof value === 'number' && Number.isFinite(value));
}

function getSeriesStats(points) {
  const values = getValidValues(points);
  if (!values.length) {
    return null;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
  return { min, max, avg };
}

function getFirstValidValue(points) {
  return (points || []).find(point => typeof point?.value === 'number' && Number.isFinite(point.value))?.value ?? null;
}

function getLastValidValue(points) {
  const validPoints = (points || []).filter(point => typeof point?.value === 'number' && Number.isFinite(point.value));
  return validPoints.length ? validPoints[validPoints.length - 1].value : null;
}

function formatMetricValue(value, decimals = 1) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '0';
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function hasRenderableSeries(points) {
  return getValidValues(points).length > 0;
}

function renderDurationSummaryValue(durationSeconds, t) {
  const totalMinutes = Math.max(0, Math.round(Number(durationSeconds || 0) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return (
      <>
        <span className="historyMetricNumber">{hours}</span>
        <span className="historyMetricUnit"> {t('historyPage.timeHours')}</span>
        <span className="historyMetricUnit"> </span>
        <span className="historyMetricNumber">{minutes}</span>
        <span className="historyMetricUnit"> {t('historyPage.timeMinutes')}</span>
      </>
    );
  }

  if (hours > 0) {
    return (
      <>
        <span className="historyMetricNumber">{hours}</span>
        <span className="historyMetricUnit"> {t('historyPage.timeHours')}</span>
      </>
    );
  }

  return (
    <>
      <span className="historyMetricNumber">{minutes}</span>
      <span className="historyMetricUnit"> {t('historyPage.timeMinutes')}</span>
    </>
  );
}

function renderPowerSummaryValue(value, unit, decimals = 2) {
  return (
    <>
      <span className="historyMetricNumber">{formatMetricValue(value, decimals)}</span>
      <span className="historyMetricUnit"> {unit}</span>
    </>
  );
}

function createGradient(chart, startColor, endColor) {
  const { ctx, chartArea } = chart;
  if (!chartArea) {
    return startColor;
  }

  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, startColor);
  gradient.addColorStop(1, endColor);
  return gradient;
}

function mergeSeries(powerPoints, voltagePoints, socPoints) {
  const timeline = new Map();

  [powerPoints, voltagePoints, socPoints].forEach(series => {
    (series || []).forEach(point => {
      const key = point?.timestamp || point?.label;
      if (!key) return;
      if (!timeline.has(key)) {
        timeline.set(key, {
          timestamp: point?.timestamp || null,
          label: point?.label || '',
          power: null,
          voltage: null,
          soc: null,
        });
      }
    });
  });

  (powerPoints || []).forEach(point => {
    const key = point?.timestamp || point?.label;
    if (timeline.has(key)) {
      timeline.get(key).power = point?.value ?? null;
    }
  });

  (voltagePoints || []).forEach(point => {
    const key = point?.timestamp || point?.label;
    if (timeline.has(key)) {
      timeline.get(key).voltage = point?.value ?? null;
    }
  });

  (socPoints || []).forEach(point => {
    const key = point?.timestamp || point?.label;
    if (timeline.has(key)) {
      timeline.get(key).soc = point?.value ?? null;
    }
  });

  return Array.from(timeline.values()).sort((a, b) => {
    const left = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const right = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return left - right;
  });
}

function isZeroSocValue(value) {
  return typeof value !== 'number' || !Number.isFinite(value) || value <= 0;
}

function isPositiveSocValue(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

const SOC_CHART_JUMP_MIN = 20;

function isSocJumpStep(previousSoc, currentSoc) {
  if (!isPositiveSocValue(currentSoc)) {
    return false;
  }

  if (isZeroSocValue(previousSoc) && currentSoc > SOC_CHART_JUMP_MIN) {
    return true;
  }

  if (
    isPositiveSocValue(previousSoc) &&
    previousSoc <= SOC_CHART_JUMP_MIN &&
    currentSoc - previousSoc > SOC_CHART_JUMP_MIN
  ) {
    return true;
  }

  return false;
}

function findSocJumpStartIndex(values) {
  if (!Array.isArray(values) || values.length < 2) {
    return -1;
  }

  for (let index = 1; index < values.length; index += 1) {
    if (isSocJumpStep(values[index - 1], values[index])) {
      return index;
    }
  }

  return -1;
}

function trimSocSeriesPoints(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return points || [];
  }

  const jumpIndex = findSocJumpStartIndex(points.map(point => point?.value));
  return jumpIndex > 0 ? points.slice(jumpIndex) : points;
}

function trimPointsFromTimestamp(points, startTimestamp) {
  if (!startTimestamp) {
    return points || [];
  }

  const startMs = new Date(startTimestamp).getTime();
  if (!Number.isFinite(startMs)) {
    return points || [];
  }

  return (points || []).filter(point => {
    const pointMs = point?.timestamp ? new Date(point.timestamp).getTime() : NaN;
    return Number.isFinite(pointMs) && pointMs >= startMs;
  });
}

function trimChartFromSocJump(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return points || [];
  }

  const jumpIndex = findSocJumpStartIndex(points.map(point => point?.soc));
  return jumpIndex > 0 ? points.slice(jumpIndex) : points;
}

function getChartStartSocValue(points) {
  const socValues = (points || []).map(point => point?.soc);
  const jumpIndex = findSocJumpStartIndex(socValues);

  if (jumpIndex > 0) {
    const jumpSoc = points[jumpIndex]?.soc;
    return typeof jumpSoc === 'number' && Number.isFinite(jumpSoc) ? jumpSoc : null;
  }

  return (
    (points || []).find(point => typeof point?.soc === 'number' && Number.isFinite(point.soc) && point.soc > 0)?.soc ??
    null
  );
}

function hasCurveValues(points) {
  return (points || []).some(point => typeof point?.value === 'number' && Number.isFinite(point.value));
}

function createCurvePoint(timestamp, value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return { timestamp, value };
}

function seedCurveFromStatus(lastJob) {
  const startedAt = lastJob?.startedOn || new Date().toISOString();
  const now = new Date().toISOString();
  const powerPoint = createCurvePoint(startedAt, lastJob?.powerWt);
  const voltagePoint = createCurvePoint(startedAt, lastJob?.voltage);
  const socPoint = createCurvePoint(startedAt, lastJob?.soc);

  const powerCurve = powerPoint ? [powerPoint, { ...powerPoint, timestamp: now }] : [];
  const voltageCurve = voltagePoint ? [voltagePoint, { ...voltagePoint, timestamp: now }] : [];
  const socCurve = socPoint && lastJob.soc > 0 ? [socPoint, { ...socPoint, timestamp: now }] : [];

  return { powerCurve, voltageCurve, socCurve };
}

function appendLiveStatusPoint(curve, value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return curve || [];
  }

  const points = [...(curve || [])];
  const now = new Date().toISOString();
  const lastPoint = points[points.length - 1];
  const lastTimestampMs = lastPoint?.timestamp ? new Date(lastPoint.timestamp).getTime() : 0;

  if (lastTimestampMs && Date.now() - lastTimestampMs < IN_PROGRESS_STATUS_POLL_MS) {
    points[points.length - 1] = { ...lastPoint, value };
    return points;
  }

  return [...points, { timestamp: now, value }];
}

function enrichCurvesFromStatus(powerCurve, voltageCurve, socCurve, lastJob) {
  if (!lastJob) {
    return { powerCurve, voltageCurve, socCurve };
  }

  if (!hasCurveValues(powerCurve)) {
    return seedCurveFromStatus(lastJob);
  }

  return {
    powerCurve: appendLiveStatusPoint(powerCurve, lastJob.powerWt),
    voltageCurve: appendLiveStatusPoint(voltageCurve, lastJob.voltage),
    socCurve: appendLiveStatusPoint(socCurve, lastJob.soc > 0 ? lastJob.soc : null),
  };
}

function smoothSeries(points) {
  const validPoints = (points || []).filter(point => typeof point?.value === 'number' && Number.isFinite(point.value));

  if (validPoints.length < 3) {
    return points || [];
  }

  const smoothedValues = validPoints.map((point, index, array) => {
    if (index === 0 || index === array.length - 1) {
      return point.value;
    }

    const prev = array[index - 1].value;
    const current = point.value;
    const next = array[index + 1].value;

    return prev * 0.2 + current * 0.6 + next * 0.2;
  });

  let validIndex = 0;
  return (points || []).map(point => {
    if (typeof point?.value !== 'number' || !Number.isFinite(point.value)) {
      return point;
    }

    const smoothedValue = smoothedValues[validIndex];
    validIndex += 1;

    return {
      ...point,
      value: smoothedValue,
    };
  });
}

function socSegmentBorderColor(ctx) {
  const value = ctx.p1?.parsed?.y ?? ctx.p0?.parsed?.y;
  return getSocColors(value).border;
}

function socPointColor(ctx) {
  return getSocColors(ctx.parsed?.y).color;
}

function findLastSocChartPoint(chart, socDatasetIndex) {
  const dataset = chart.data.datasets[socDatasetIndex];
  const meta = chart.getDatasetMeta(socDatasetIndex);
  if (!dataset || !meta?.data?.length) {
    return null;
  }

  for (let index = meta.data.length - 1; index >= 0; index -= 1) {
    const value = dataset.data[index];
    const point = meta.data[index];
    if (point && !point.skip && typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return { point, value };
    }
  }

  return null;
}

function createSocTipLabelPlugin(showSocLabel, lastSocValue) {
  return {
    id: 'historySocTipLabel',
    afterDraw(chart) {
      if (!showSocLabel) {
        return;
      }

      const socDatasetIndex = chart.data.datasets.findIndex(dataset => dataset.label === 'soc' && !dataset.hidden);
      if (socDatasetIndex < 0) {
        return;
      }

      const lastSocPoint = findLastSocChartPoint(chart, socDatasetIndex);
      if (!lastSocPoint) {
        return;
      }

      const value =
        typeof lastSocValue === 'number' && Number.isFinite(lastSocValue) && lastSocValue > 0
          ? lastSocValue
          : lastSocPoint.value;
      const { point } = lastSocPoint;
      const { ctx, chartArea } = chart;
      if (!chartArea) {
        return;
      }

      const palette = getSocColors(value);
      const label = `${Math.round(value)}%`;
      const fontSize = chart.width <= 360 ? 11 : 12;
      const paddingX = 7;
      const paddingY = 4;
      const gap = 6;
      const edgeGap = 2;

      ctx.save();
      ctx.font = `700 ${fontSize}px ${HISTORY_CHART_FONT}`;
      const textWidth = ctx.measureText(label).width;
      const badgeWidth = textWidth + paddingX * 2;
      const badgeHeight = fontSize + paddingY * 2;
      const maxBadgeX = chartArea.right - badgeWidth - edgeGap;
      const minBadgeX = chartArea.left + edgeGap;

      let badgeX = point.x + gap;
      if (badgeX + badgeWidth > chartArea.right - edgeGap) {
        badgeX = point.x - badgeWidth - gap;
      }
      badgeX = Math.max(minBadgeX, Math.min(badgeX, maxBadgeX));

      let badgeY = point.y - badgeHeight / 2;
      badgeY = Math.max(chartArea.top + 2, Math.min(badgeY, chartArea.bottom - badgeHeight - 2));

      ctx.beginPath();
      ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = palette.color;
      ctx.shadowColor = palette.color;
      ctx.shadowBlur = 8;
      ctx.fill();
      ctx.shadowBlur = 0;

      ctx.fillStyle = 'rgba(18, 8, 24, 0.9)';
      ctx.strokeStyle = palette.border;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = palette.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, badgeX + badgeWidth / 2, badgeY + badgeHeight / 2);
      ctx.restore();
    },
  };
}

function buildChartData(points, visibleSeries, hasSocChart, isMobileView, lastSocValue) {
  const firstTimestamp = points.find(point => point?.timestamp)?.timestamp || null;
  const labels = points.map(point =>
    isMobileView ? formatElapsedMinutesLabel(point.timestamp, firstTimestamp) : point.label
  );
  const socPalette = getSocColors(lastSocValue);
  const jumpStartIndex = hasSocChart ? findSocJumpStartIndex(points.map(point => point?.soc)) : -1;
  const socData = points.map((point, index) => {
    if (!hasSocChart) {
      return point?.soc;
    }
    if (jumpStartIndex > 0 && index < jumpStartIndex) {
      return null;
    }
    return point?.soc;
  });
  const powerData = points.map((point, index) => {
    if (!hasSocChart) {
      return point?.power;
    }
    if (jumpStartIndex > 0 && index < jumpStartIndex) {
      return null;
    }
    return point?.power;
  });

  return {
    labels,
    datasets: [
      {
        label: 'power',
        data: powerData,
        yAxisID: 'power',
        borderColor: POWER_CHART_BORDER,
        backgroundColor: context => createGradient(context.chart, POWER_CHART_FILL_TOP, POWER_CHART_FILL_BOTTOM),
        fill: true,
        tension: 0.42,
        cubicInterpolationMode: 'default',
        stepped: false,
        borderCapStyle: 'round',
        borderJoinStyle: 'round',
        pointRadius: 0,
        pointHoverRadius: 0,
        pointBackgroundColor: POWER_CHART_POINT,
        pointBorderColor: POWER_CHART_POINT_BORDER,
        pointBorderWidth: 1,
        borderWidth: 3,
        spanGaps: true,
      },
      {
        label: 'voltage',
        data: points.map(point => point.voltage),
        yAxisID: 'voltage',
        borderColor: '#59a9ff',
        backgroundColor: 'rgba(89, 169, 255, 0)',
        fill: false,
        hidden: !visibleSeries.voltage,
        tension: 0.42,
        cubicInterpolationMode: 'default',
        stepped: false,
        borderCapStyle: 'round',
        borderJoinStyle: 'round',
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 2,
        spanGaps: true,
      },
      ...(hasSocChart
        ? [
            {
              label: 'soc',
              data: socData,
              yAxisID: 'soc',
              borderColor: socPalette.border,
              backgroundColor: socPalette.fill,
              fill: false,
              hidden: !visibleSeries.soc,
              tension: 0.4,
              cubicInterpolationMode: 'default',
              stepped: false,
              borderCapStyle: 'round',
              borderJoinStyle: 'round',
              pointRadius: 0,
              pointHoverRadius: 0,
              pointBackgroundColor: socPointColor,
              pointBorderColor: socPointColor,
              borderWidth: 2,
              spanGaps: true,
              segment: {
                borderColor: socSegmentBorderColor,
              },
            },
          ]
        : []),
    ],
  };
}

function getMinuteTickStep(elapsedMinutes, maxTicksLimit) {
  const validMinutes = elapsedMinutes.filter(value => typeof value === 'number' && Number.isFinite(value));
  const lastMinute = validMinutes.length ? validMinutes[validMinutes.length - 1] : 0;

  if (lastMinute <= 0 || maxTicksLimit <= 1) {
    return 1;
  }

  return Math.max(1, Math.ceil(lastMinute / Math.max(1, maxTicksLimit - 1)));
}

function getMaxVisibleXTicks(pointsCount, isMobileView) {
  if (isMobileView) {
    if (pointsCount > 18) {
      return 2;
    }
    if (pointsCount > 8) {
      return 3;
    }
    return 4;
  }

  if (pointsCount > 24) {
    return 6;
  }
  return 8;
}

function roundAxisMaxFromPeak(peakKw) {
  if (peakKw <= 11) {
    return Math.max(5, Math.ceil(peakKw / 2) * 2);
  }
  if (peakKw <= 30) {
    return Math.ceil(peakKw / 5) * 5;
  }
  if (peakKw <= 100) {
    return Math.ceil(peakKw / 10) * 10;
  }
  if (peakKw <= 250) {
    return Math.ceil(peakKw / 25) * 25;
  }
  return Math.ceil(peakKw / 50) * 50;
}

/** Y-axis top: station rated max (kW), or higher if session peak exceeds it. */
function getPowerAxisMax(powerStats, stationMaxKw) {
  const observedMax = typeof powerStats?.max === 'number' && Number.isFinite(powerStats.max) ? powerStats.max : 0;

  if (typeof stationMaxKw === 'number' && stationMaxKw > 0) {
    if (observedMax <= stationMaxKw) {
      return stationMaxKw;
    }
    return roundAxisMaxFromPeak(observedMax);
  }

  if (observedMax > 0) {
    return roundAxisMaxFromPeak(observedMax);
  }

  return 100;
}

function buildChartOptions(
  t,
  visibleSeries,
  hasSocChart,
  pointsCount,
  isMobileView,
  chartStartSocValue,
  socStats,
  powerStats,
  stationMaxKw,
  elapsedMinutes,
  lastSocValue,
  externalTooltip
) {
  const xTickLimit = getMaxVisibleXTicks(pointsCount, isMobileView);
  const minuteTickStep = getMinuteTickStep(elapsedMinutes, xTickLimit);
  const socPalette = getSocColors(lastSocValue);

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: {
        top: isMobileView ? 10 : 16,
        right: isMobileView ? 6 : 10,
        bottom: isMobileView ? 8 : 12,
        left: isMobileView ? 2 : 6,
      },
    },
    // Click is omitted so a tap does not leave the tooltip up after the finger lifts.
    events: ['mousemove', 'mouseout', 'touchstart', 'touchmove'],
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: externalTooltip,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: 'rgba(218, 209, 230, 0.72)',
          align: isMobileView ? 'inner' : 'center',
          autoSkip: !isMobileView,
          autoSkipPadding: isMobileView ? 28 : 10,
          maxTicksLimit: xTickLimit,
          padding: isMobileView ? 10 : 4,
          font: {
            family: HISTORY_CHART_FONT,
            size: isMobileView ? 10 : 12,
            weight: '700',
          },
          callback: function callback(value, index) {
            if (!isMobileView) {
              return this.getLabelForValue(value);
            }

            const minute = elapsedMinutes[index];
            if (typeof minute !== 'number' || !Number.isFinite(minute)) {
              return null;
            }

            const previousMinute = index > 0 ? elapsedMinutes[index - 1] : null;
            const isFirstTick = index === 0 || minute === 0;
            const isLastTick = index === elapsedMinutes.length - 1;
            const isNewMinute = previousMinute !== minute;

            if (!isNewMinute) {
              return null;
            }

            if (isFirstTick || isLastTick || minute % minuteTickStep === 0) {
              return `${minute}m`;
            }

            return null;
          },
          minRotation: 0,
          maxRotation: 0,
        },
      },
      power: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        min: 0,
        max: getPowerAxisMax(powerStats, stationMaxKw),
        grid: {
          color: 'rgba(255, 255, 255, 0.08)',
        },
        ticks: {
          color: '#dad1e6',
          callback: value => `${formatMetricValue(Number(value), 0)}`,
          font: {
            family: HISTORY_CHART_FONT,
            size: 12,
            weight: '700',
          },
        },
        title: {
          display: true,
          text: `${t('historyPage.power')}, ${t('kW')}`,
          color: 'rgba(218, 209, 230, 0.8)',
          font: {
            family: HISTORY_CHART_FONT,
            size: 14,
            weight: '700',
          },
        },
      },
      voltage: {
        type: 'linear',
        position: 'right',
        display: false,
        min: 0,
        max: 1000,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#59a9ff',
          callback: value => `${formatMetricValue(Number(value), 0)} ${t('v')}`,
          font: {
            family: HISTORY_CHART_FONT,
            size: 12,
            weight: '700',
          },
        },
        title: {
          display: false,
          text: `${t('historyPage.voltage')}, ${t('v')}`,
          color: '#59a9ff',
          font: {
            family: HISTORY_CHART_FONT,
            size: 14,
            weight: '700',
          },
        },
      },
      soc: {
        type: 'linear',
        position: 'right',
        display: false,
        suggestedMin: chartStartSocValue ?? undefined,
        suggestedMax: socStats?.max ?? undefined,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: socPalette.color,
          callback: value => `${formatMetricValue(Number(value), 0)}%`,
          font: {
            family: HISTORY_CHART_FONT,
            size: 12,
            weight: '700',
          },
        },
        title: {
          display: false,
          text: t('historyPage.soc'),
          color: socPalette.color,
          font: {
            family: HISTORY_CHART_FONT,
            size: 14,
            weight: '700',
          },
        },
      },
    },
  };
}

export default function HistoryPowerCurve({
  jobId,
  clientUiId,
  state,
  durationSeconds,
  energyWth,
  expanded,
  inModal = false,
  plugType,
  stationNumber,
  publicStation = false,
}) {
  const { t } = useTranslation();
  const isInProgress = state === 'IN_PROGRESS';
  const isCompletedWithEnergy = !isInProgress && Number(energyWth) > 0;
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });
  const useStationCurve = Boolean(publicStation && stationNumber);
  const shouldRender = useStationCurve ? true : Boolean(jobId) && (isInProgress || isCompletedWithEnergy);
  const shouldSkipClient = !expanded || !shouldRender || !clientUiId || !jobId || useStationCurve;
  const shouldSkipStation = !expanded || !useStationCurve;
  const livePollMs = isInProgress && expanded ? IN_PROGRESS_POWER_CURVE_POLL_MS : undefined;

  const clientCurveQuery = useGetHistoryPowerCurveQuery(
    { clientUiId, jobId },
    {
      skip: shouldSkipClient,
      pollingInterval: livePollMs,
    }
  );
  const stationCurveQuery = useGetStationPowerCurveQuery(
    { stationNumber },
    {
      skip: shouldSkipStation,
      pollingInterval: livePollMs,
    }
  );
  const { data, isLoading, isFetching, isError } = useStationCurve ? stationCurveQuery : clientCurveQuery;

  const { data: stationStatus } = useGetStationStatusQuery(
    { stationNumber },
    {
      skip: !expanded || !shouldRender || !stationNumber || !isInProgress,
      pollingInterval: isInProgress && expanded ? IN_PROGRESS_STATUS_POLL_MS : undefined,
    }
  );

  const liveLastJob = useMemo(() => {
    const lastJob = stationStatus?.lastJob;
    if (!lastJob || lastJob.state !== 'IN_PROGRESS') {
      return null;
    }
    return lastJob;
  }, [stationStatus?.lastJob]);

  const curveData = useMemo(() => {
    if (!isInProgress || !liveLastJob) {
      return {
        powerCurve: data?.powerCurve,
        voltageCurve: data?.voltageCurve,
        socCurve: data?.socCurve,
      };
    }

    return enrichCurvesFromStatus(data?.powerCurve, data?.voltageCurve, data?.socCurve, liveLastJob);
  }, [data?.powerCurve, data?.voltageCurve, data?.socCurve, isInProgress, liveLastJob]);

  const { data: stationData } = api.useGetDeviceStationQuery(
    { stationNumber, lat: 0, lon: 0 },
    { skip: !expanded || !shouldRender || !stationNumber || Boolean(data?.maxPowerWt) }
  );

  const [visibleSeries, setVisibleSeries] = useState({
    voltage: false,
    soc: true,
  });

  const rawPowerPoints = useMemo(() => normalizeSeries(curveData?.powerCurve, 1000), [curveData?.powerCurve]);
  const rawVoltagePoints = useMemo(() => normalizeSeries(curveData?.voltageCurve), [curveData?.voltageCurve]);
  const rawSocPoints = useMemo(() => normalizeSeries(curveData?.socCurve), [curveData?.socCurve]);
  const powerPoints = useMemo(() => smoothSeries(rawPowerPoints), [rawPowerPoints]);
  const voltagePoints = useMemo(() => smoothSeries(rawVoltagePoints), [rawVoltagePoints]);
  const socPoints = useMemo(() => smoothSeries(rawSocPoints), [rawSocPoints]);
  const powerStats = useMemo(() => getSeriesStats(rawPowerPoints), [rawPowerPoints]);
  const stationMaxKw = useMemo(() => {
    const watts = data?.maxPowerWt ?? stationData?.maxPowerWt;
    return typeof watts === 'number' && watts > 0 ? watts / 1000 : null;
  }, [data?.maxPowerWt, stationData?.maxPowerWt]);
  const isDcSession = useMemo(
    () => isDcPort(plugType, data?.maxPowerWt ?? stationData?.maxPowerWt),
    [plugType, data?.maxPowerWt, stationData?.maxPowerWt]
  );
  const hasSocChart = useMemo(() => {
    if (!isDcSession) {
      return false;
    }
    return hasRenderableSeries(rawSocPoints);
  }, [isDcSession, rawSocPoints]);
  const lastSocValue = useMemo(() => getLastValidValue(rawSocPoints), [rawSocPoints]);
  const trimmedSocPoints = useMemo(() => trimSocSeriesPoints(socPoints), [socPoints]);
  const socJumpStartTimestamp = useMemo(() => trimmedSocPoints[0]?.timestamp ?? null, [trimmedSocPoints]);
  const mergedPoints = useMemo(
    () => mergeSeries(powerPoints, voltagePoints, socPoints),
    [powerPoints, voltagePoints, socPoints]
  );
  const combinedPoints = useMemo(() => {
    let points = mergedPoints;

    if (socJumpStartTimestamp && trimmedSocPoints.length < socPoints.length) {
      points = trimPointsFromTimestamp(points, socJumpStartTimestamp);
    }

    return trimChartFromSocJump(points);
  }, [mergedPoints, socJumpStartTimestamp, trimmedSocPoints.length, socPoints.length]);
  const chartStartSocValue = useMemo(() => getChartStartSocValue(combinedPoints), [combinedPoints]);
  const socStats = useMemo(() => getSeriesStats(rawSocPoints), [rawSocPoints]);
  const elapsedMinutes = useMemo(() => {
    const firstTimestamp = combinedPoints.find(point => point?.timestamp)?.timestamp || null;
    return combinedPoints.map(point => getElapsedMinutesValue(point.timestamp, firstTimestamp));
  }, [combinedPoints]);
  const socDelta = useMemo(() => {
    if (typeof chartStartSocValue !== 'number' || typeof lastSocValue !== 'number') {
      return null;
    }
    return lastSocValue - chartStartSocValue;
  }, [chartStartSocValue, lastSocValue]);
  const showSocChart = hasSocChart && !isFlatSocImplausible(energyWth, socDelta);
  const chartData = useMemo(
    () => buildChartData(combinedPoints, visibleSeries, showSocChart, isMobileView, lastSocValue),
    [combinedPoints, visibleSeries, showSocChart, isMobileView, lastSocValue]
  );
  const socLegendStyle = useMemo(() => ({ background: getSocColors(lastSocValue).legend }), [lastSocValue]);
  const chartPlugins = useMemo(
    () => [
      createPowerChartHoverCrosshairPlugin(),
      createPowerChartTouchReleasePlugin(),
      createSocTipLabelPlugin(showSocChart && visibleSeries.soc, lastSocValue),
    ],
    [showSocChart, visibleSeries.soc, lastSocValue]
  );
  const chartExternalTooltip = useMemo(() => {
    const firstTimestamp = combinedPoints.find(point => point?.timestamp)?.timestamp || null;

    return createPowerChartExternalTooltip(dataIndex => {
      const point = typeof dataIndex === 'number' ? combinedPoints[dataIndex] : null;
      if (!point) {
        return null;
      }

      return {
        timeLabel: isMobileView
          ? formatElapsedMinutesLabel(point.timestamp, firstTimestamp)
          : formatTimestampLabel(point.timestamp),
        power: point.power,
        voltage: point.voltage,
        soc: showSocChart ? point.soc : null,
      };
    }, t);
  }, [combinedPoints, showSocChart, isMobileView, t]);
  const chartOptions = useMemo(
    () =>
      buildChartOptions(
        t,
        visibleSeries,
        showSocChart,
        combinedPoints.length,
        isMobileView,
        chartStartSocValue,
        socStats,
        powerStats,
        stationMaxKw,
        elapsedMinutes,
        lastSocValue,
        chartExternalTooltip
      ),
    [
      t,
      visibleSeries,
      showSocChart,
      combinedPoints,
      isMobileView,
      chartStartSocValue,
      socStats,
      powerStats,
      stationMaxKw,
      elapsedMinutes,
      lastSocValue,
      chartExternalTooltip,
    ]
  );
  const hasAnyData = useMemo(
    () =>
      hasRenderableSeries(rawPowerPoints) || hasRenderableSeries(rawVoltagePoints) || hasRenderableSeries(rawSocPoints),
    [rawPowerPoints, rawVoltagePoints, rawSocPoints]
  );
  const sessionAvgPowerKw = useMemo(
    () => computeSessionAvgPowerKw(energyWth, durationSeconds),
    [energyWth, durationSeconds]
  );
  const showLoadingState = isLoading && !hasAnyData;
  const summaryItems = useMemo(() => {
    const items = [];

    if (Number(durationSeconds) > 0) {
      items.push({
        label: t('historyPage.summaryTimeSpent'),
        renderValue: renderDurationSummaryValue(durationSeconds, t),
      });
    }

    if (showSocChart && typeof chartStartSocValue === 'number' && typeof socDelta === 'number') {
      items.push({
        label: t('historyPage.summarySocLabel'),
        renderValue: formatSocSummaryLine(t, chartStartSocValue, socDelta, chartStartSocValue + socDelta),
      });
    }

    if (sessionAvgPowerKw != null) {
      items.push({
        label: t('historyPage.summaryAvgPower'),
        renderValue: renderPowerSummaryValue(sessionAvgPowerKw, t('kW'), 1),
      });
    } else if (powerStats?.avg != null) {
      items.push({
        label: t('historyPage.summaryAvgPower'),
        renderValue: renderPowerSummaryValue(powerStats.avg, t('kW'), 1),
      });
    }

    if (powerStats?.max != null) {
      items.push({
        label: t('historyPage.summaryPeakPower'),
        renderValue: renderPowerSummaryValue(powerStats.max, t('kW')),
      });
    }

    return items;
  }, [durationSeconds, chartStartSocValue, showSocChart, sessionAvgPowerKw, powerStats, socDelta, t]);

  useEffect(() => {
    setVisibleSeries(prev => {
      if (!showSocChart && prev.soc) {
        return { ...prev, soc: false };
      }
      if (showSocChart && !prev.soc) {
        return { ...prev, soc: true };
      }
      return prev;
    });
  }, [showSocChart]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleViewportChange = event => {
      setIsMobileView(event.matches);
    };

    setIsMobileView(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    if (!expanded) {
      hidePowerChartTooltip();
    }
    return () => hidePowerChartTooltip();
  }, [expanded]);

  if (!expanded || !shouldRender) {
    return null;
  }

  return (
    <section className={inModal ? `${styles.section} ${styles.sectionInModal}` : styles.section}>
      {!inModal && (
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{t('historyPage.powerCurve')}</h3>
          {(isLoading || isFetching) && <span className={styles.status}>{t('historyPage.loading')}</span>}
          {!isLoading && !isFetching && isError && (
            <span className={styles.status}>{t('historyPage.noPowerData')}</span>
          )}
        </div>
      )}

      <div className={inModal ? `${styles.chartCard} ${styles.chartCardInModal}` : styles.chartCard}>
        {showLoadingState ? (
          <div className={styles.placeholder}>{t('historyPage.loading')}</div>
        ) : hasAnyData ? (
          <>
            <div className={styles.powerCurvePlot}>
              <div className={styles.chartWrap}>
                <div className={styles.chartBox}>
                  <Line data={chartData} options={chartOptions} plugins={chartPlugins} />
                </div>
              </div>
            </div>

            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <span className={`${styles.legendLine} ${styles.legendLinePower}`} />
                <span>{t('historyPage.power')}</span>
              </div>

              {showSocChart && (
                <button
                  type="button"
                  className={`${styles.legendItem} ${visibleSeries.soc ? styles.legendToggleActive : styles.legendToggle}`}
                  onClick={() => setVisibleSeries(prev => ({ ...prev, soc: !prev.soc }))}
                >
                  <span className={styles.legendLine} style={socLegendStyle} />
                  <span>{t('historyPage.soc')}</span>
                </button>
              )}

              <button
                type="button"
                className={`${styles.legendItem} ${visibleSeries.voltage ? styles.legendToggleActive : styles.legendToggle}`}
                onClick={() => setVisibleSeries(prev => ({ ...prev, voltage: !prev.voltage }))}
              >
                <span className={`${styles.legendLine} ${styles.legendLineVoltage}`} />
                <span>{t('historyPage.voltage')}</span>
              </button>
            </div>

            {summaryItems.length > 0 && (
              <div className={styles.summaryPanel}>
                {summaryItems.map(item => (
                  <div key={item.label} className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>{item.label}</span>
                    <span className={styles.summaryValue}>{item.renderValue}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className={styles.placeholder}>{t('historyPage.noPowerData')}</div>
        )}
      </div>
    </section>
  );
}
